import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Sparkles, ArrowRight, GraduationCap, RefreshCw, Mail, Loader2 } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import Seo from '../components/Seo';
import { LEVEL_TEST_QUESTIONS, computeLevelResult } from '../config/levelTest';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestDeNivelPage() {
  const [answers, setAnswers] = useState({});
  const [stage, setStage] = useState('quiz'); // quiz | result
  const [planSent, setPlanSent] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const allAnswered = LEVEL_TEST_QUESTIONS.every((q) => answers[q.id] !== undefined);

  const preview = useMemo(() => {
    let correct = 0;
    LEVEL_TEST_QUESTIONS.forEach((q) => {
      if (answers[q.id] === q.correct) correct += 1;
    });
    return { correct, total: LEVEL_TEST_QUESTIONS.length, ...computeLevelResult(correct) };
  }, [answers]);

  const setAnswer = (qid, idx) => {
    if (stage === 'quiz') setAnswers((prev) => ({ ...prev, [qid]: idx }));
  };

  const reset = () => {
    setAnswers({});
    setStage('quiz');
    setEmail('');
    setName('');
    setError('');
    setPlanSent(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showMyResult = () => {
    if (!allAnswered) return;
    setStage('result');
    setTimeout(() => {
      document.getElementById('test-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Introduce un email válido.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/level-test/submit`, {
        email: trimmed,
        name: name.trim() || null,
        level: preview.level,
        correct: preview.correct,
        total: preview.total,
      });
      setPlanSent(true);
    } catch (err) {
      setError('No pudimos enviarte el plan. Inténtalo de nuevo en unos segundos.');
    } finally {
      setSubmitting(false);
    }
  };

  const showResult = stage === 'result';
  const showFeedback = stage !== 'quiz';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8F5FA] via-white to-white">
      <Seo
        path="/test-de-nivel"
        fullTitle="Test de Nivel de Español Gratuito | ¿Cuál es tu nivel?"
        description="Descubre tu nivel de español en menos de 5 minutos con nuestra prueba gratuita online. Recibe tu diagnóstico de inmediato."
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-10 px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-5 bg-[#A8D8EA33] text-[#4A4A4A]">
          <Sparkles className="w-3.5 h-3.5" />
          TEST GRATUITO · 20 PREGUNTAS · 8 MINUTOS
        </div>
        <h1
          className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#2F2F2F] leading-tight tracking-tight mb-4"
          data-testid="test-h1"
        >
          Descubre tu nivel de español gratis
        </h1>
        <p className="max-w-2xl mx-auto text-lg text-gray-600">
          20 preguntas graduadas y sabrás si estás en A1, A2, B1, B2, C1 o C2. Diagnóstico inmediato con
          plan sugerido de aprendizaje.
        </p>
      </section>

      {/* Quiz */}
      <section className="pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {LEVEL_TEST_QUESTIONS.map((q, qIdx) => (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.35, delay: qIdx * 0.02 }}
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
              data-testid={`test-question-${q.id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold text-[#7DBDD9]">
                  PREGUNTA {qIdx + 1} / {LEVEL_TEST_QUESTIONS.length}
                </span>
              </div>
              <p className="text-lg font-semibold text-[#2F2F2F] mb-4">{q.prompt}</p>
              <div className="space-y-2">
                {q.options.map((opt, oIdx) => {
                  const selected = answers[q.id] === oIdx;
                  const isCorrect = showFeedback && oIdx === q.correct;
                  const isWrong = showFeedback && selected && oIdx !== q.correct;
                  return (
                    <button
                      key={oIdx}
                      type="button"
                      onClick={() => setAnswer(q.id, oIdx)}
                      data-testid={`test-option-${q.id}-${oIdx}`}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                        isCorrect
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : isWrong
                          ? 'border-red-400 bg-red-50 text-red-800'
                          : selected
                          ? 'border-[#7DBDD9] bg-[#A8D8EA22] text-[#2F2F2F]'
                          : 'border-gray-200 bg-white hover:border-[#A8D8EA] text-[#4A4A4A]'
                      }`}
                      disabled={stage !== 'quiz'}
                    >
                      <span className="font-medium">{opt}</span>
                      {isCorrect && <CheckCircle2 className="w-5 h-5 float-right text-green-600" />}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ))}

          {/* Submit → email step */}
          {stage === 'quiz' && (
            <div className="pt-4 text-center">
              <button
                onClick={showMyResult}
                disabled={!allAnswered}
                data-testid="test-submit"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-gradient-to-r from-[#F5A6A4] to-[#7DBDD9] text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ver mi nivel
                <ArrowRight className="w-5 h-5" />
              </button>
              {!allAnswered && (
                <p className="text-sm text-gray-500 mt-3">
                  Responde las 20 preguntas para ver tu diagnóstico.
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Result */}
      <AnimatePresence>
        {showResult && (
          <motion.section
            id="test-result"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="pb-24 px-4 sm:px-6 lg:px-8"
            data-testid="test-result"
          >
            <div className="max-w-2xl mx-auto rounded-3xl p-10 md:p-12 text-center shadow-xl overflow-hidden relative bg-gradient-to-br from-[#A8D8EA] via-[#B5DFEC] to-[#F5A6A4]">
              <GraduationCap className="w-14 h-14 text-white mx-auto mb-4 drop-shadow" />
              <p className="text-sm font-bold tracking-wider text-white/90 uppercase mb-2">Tu resultado</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-2">
                Tu nivel estimado es <span className="underline decoration-wavy">{preview.label}</span>
              </h2>
              <p className="text-white/95 text-lg mb-6">
                Has respondido correctamente <strong>{preview.correct}/{preview.total}</strong> preguntas.
              </p>
              <p className="text-white/90 mb-8 max-w-md mx-auto">
                Este es un diagnóstico orientativo. Para un plan personalizado, reserva una clase gratuita
                y hacemos una radiografía real de tu español.
              </p>
              {planSent && (
                <p className="text-white text-sm font-semibold mb-6 inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2" data-testid="test-email-sent-note">
                  <Mail className="w-4 h-4" /> Te hemos enviado el resultado y tu plan a {email.trim().toLowerCase()}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/#booking"
                  data-testid="test-cta-booking"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#2F2F2F] font-bold shadow-lg hover:shadow-2xl transition-all hover:-translate-y-0.5"
                >
                  Reservar clase de prueba GRATIS
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to={`/${preview.slug}`}
                  data-testid="test-cta-level"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#2F2F2F] text-white font-bold shadow-lg hover:shadow-2xl transition-all hover:-translate-y-0.5"
                >
                  Ver curso {preview.level}
                </Link>
              </div>
              <button
                onClick={reset}
                data-testid="test-retry"
                className="mt-6 inline-flex items-center gap-2 text-sm text-white/90 underline underline-offset-4 hover:text-white"
              >
                <RefreshCw className="w-4 h-4" /> Repetir el test
              </button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Plan por email: opcional, después de ver el resultado */}
      <AnimatePresence>
        {showResult && (
          <motion.section
            id="email-step"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="pb-20 px-4 sm:px-6 lg:px-8"
          >
            {planSent ? (
              <div
                className="max-w-xl mx-auto bg-white rounded-3xl shadow-xl p-8 md:p-10 border border-gray-100 text-center"
                data-testid="test-plan-sent"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#A8D8EA22] flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-[#7DBDD9]" />
                </div>
                <h2 className="text-2xl font-extrabold text-[#2F2F2F] mb-2">¡Plan enviado!</h2>
                <p className="text-gray-600">
                  Te he enviado a <strong>{email.trim().toLowerCase()}</strong> tu resultado {preview.label} y
                  un plan con lo que conviene trabajar primero. Revisa también la carpeta de promociones.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submitEmail}
                className="max-w-xl mx-auto bg-white rounded-3xl shadow-xl p-8 md:p-10 border border-gray-100"
                data-testid="test-email-form"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-[#A8D8EA22] flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#7DBDD9]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#7DBDD9] uppercase tracking-wider">Opcional</p>
                    <h2 className="text-2xl font-extrabold text-[#2F2F2F]">
                      ¿Te envío tu plan de estudio {preview.level}?
                    </h2>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">
                  Ya tienes tu resultado aquí arriba. Si quieres, te mando por email qué conviene
                  trabajar primero en tu nivel y la ruta recomendada. Sin spam — sólo contenido útil.
                </p>
                <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Nombre (opcional)</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="test-email-name"
                  placeholder="Tu nombre"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#7DBDD9] focus:ring-2 focus:ring-[#A8D8EA55] outline-none transition-all mb-4"
                />
                <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="test-email-input"
                  placeholder="tu@email.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#7DBDD9] focus:ring-2 focus:ring-[#A8D8EA55] outline-none transition-all mb-4"
                />
                {error && (
                  <p className="text-sm text-red-600 mb-3" data-testid="test-email-error">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="test-email-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#F5A6A4] to-[#7DBDD9] text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Enviando...
                    </>
                  ) : (
                    <>
                      Enviarme mi plan <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  Al enviar aceptas recibir el plan por email. Puedes darte de baja cuando quieras.
                </p>
              </form>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
